import { useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { users as userQuerys } from "@nualang/nualang-api-and-queries/Queries";
import { getUserIgnoreCache, updateUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import { deleteSubscription } from "@nualang/nualang-api-and-queries/APIs/Payments";
import { getGroups } from "@nualang/nualang-api-and-queries/APIs/Groups";
import { DataType, invalidateCache } from "@nualang/nualang-api-and-queries/APIs/invalidation";
import { useApp } from "./";
import config from "../config";
import logger from "../logger";

export default function useSubscription() {
  const { user, openSnackbar, startLoading, stopLoading, setCreatorSubscription, creatorSubscription } = useApp();

  const fetchSubscription = useCallback(async (userId) => {
    try {
      if (userId) {
        const currentUser = await getUserIgnoreCache(userId, true);
        if (currentUser.errorMessage) {
          throw new Error(currentUser.errorMessage);
        }
        let {
          subscriptionPlan,
          subscriptionStatus,
          subscriptionPeriodEnd,
          stripeCustomerId,
          role,
          verificationStatus,
        } = currentUser;

        let currentPlan = subscriptionPlan || "basic";
        const isWaysideTeacher =
          role === "teach" && userId.toLowerCase().includes("wayside");
        let isUpgradePossible =
          role === "teach" &&
          !isWaysideTeacher &&
          (currentPlan === "basic" ||
            [
              "trialing",
              "trialing_expired",
              "incomplete",
              "incomplete_expired",
              "past_due",
              "canceled",
              "unpaid",
              "active_expired",
            ].includes(subscriptionStatus));
        let isPaidUser =
          isWaysideTeacher ||
          (role === "teach" &&
            ["teach", "teacher_pro"].includes(currentPlan) &&
            DateTime.fromSeconds(subscriptionPeriodEnd) > DateTime.now());
        const groupsResponse = await getGroups({
          memberId: userId,
          limit: config.limits.groups,
        });
        const myGroups = groupsResponse.Items;
        // If no active subscription found check group subscriptions
        if (!isPaidUser && myGroups.length) {
          let i = 0;
          let groupSubscription, currentPlanTemp, isPaidUserTemp;
          while (!isPaidUser && myGroups[i]) {
            groupSubscription = myGroups[i];
            currentPlanTemp = groupSubscription.subscriptionPlan || "basic";
            isPaidUserTemp =
              ["teach", "teacher_pro"].includes(currentPlanTemp) &&
              DateTime.fromSeconds(groupSubscription.subscriptionPeriodEnd) >
                DateTime.now();
            if (isPaidUserTemp) {
              subscriptionPlan = groupSubscription.subscriptionPlan;
              subscriptionStatus = groupSubscription.subscriptionStatus;
              subscriptionPeriodEnd = groupSubscription.subscriptionPeriodEnd;
              currentPlan = groupSubscription.subscriptionPlan || "basic";
              isUpgradePossible = role === "teach" && currentPlan === "basic";
              isPaidUser = isPaidUserTemp;
            }
            i++;
          }
        }
        return {
          ...subscription,
          plan: currentPlan,
          isUpgradePossible,
          isPaidUser,
          stripeCustomerId,
          subscriptionStatus,
          subscriptionPeriodEnd,
          isSubscriptionLoaded: true,
          groupSubscriptions: myGroups,
          verificationStatus,
        };
      }
    } catch (error) {
      logger.error(error);
      return false;
    }
  }, []);

  const subscriptionQuery = userQuerys.useSubscription(
    async (userId) => {
      const response = fetchSubscription(userId);
      return response;
    },
    {
      userId: user?.username,
    },
    {
      enabled: !!user?.username,
    },
  );

  const subscription = useMemo(
    () =>
      subscriptionQuery.isSuccess &&
      subscriptionQuery.data &&
      subscriptionQuery.data.plan
        ? subscriptionQuery.data
        : {
            isSubscriptionLoaded: false,
            isLoading: subscriptionQuery.isLoading || subscriptionQuery.isFetching,
            isError: subscriptionQuery.isError,
          },
    [
      subscriptionQuery.data,
      subscriptionQuery.isSuccess,
      subscriptionQuery.isLoading,
      subscriptionQuery.isFetching,
      subscriptionQuery.isError,
    ],
  );

  const fetchCreatorSubscription = useCallback(async (userId) => {
    try {
      if (userId) {
        const creatorUser = await getUserIgnoreCache(userId, true);
        if (creatorUser.errorMessage) {
          throw new Error(creatorUser.errorMessage);
        }
        let {
          subscriptionPlan,
          subscriptionStatus,
          subscriptionPeriodEnd,
          stripeCustomerId,
          role,
          verificationStatus,
        } = creatorUser;

        let currentPlan = subscriptionPlan || "basic";
        let isUpgradePossible = role === "teach" && currentPlan === "basic";
        const isWaysideTeacher =
          role === "teach" && userId.toLowerCase().includes("wayside");
        let isPaidUser =
          isWaysideTeacher ||
          (role === "teach" &&
            ["teach", "teacher_pro"].includes(currentPlan) &&
            DateTime.fromSeconds(subscriptionPeriodEnd) > DateTime.now());
        const groupsResponse = await getGroups({
          memberId: userId,
          limit: config.limits.groups,
        });
        const myGroups = groupsResponse.Items;
        // If no active subscription found check group subscriptions
        if (!isPaidUser && myGroups.length) {
          let i = 0;
          let groupSubscription, currentPlanTemp, isPaidUserTemp;
          while (!isPaidUser && myGroups[i]) {
            groupSubscription = myGroups[i];
            currentPlanTemp = groupSubscription.subscriptionPlan || "basic";
            isPaidUserTemp =
              role === "teach" &&
              ["teach", "teacher_pro"].includes(currentPlanTemp) &&
              DateTime.fromSeconds(groupSubscription.subscriptionPeriodEnd) >
                DateTime.now();
            if (isPaidUserTemp) {
              subscriptionPlan = groupSubscription.subscriptionPlan;
              subscriptionStatus = groupSubscription.subscriptionStatus;
              subscriptionPeriodEnd = groupSubscription.subscriptionPeriodEnd;
              currentPlan = groupSubscription.subscriptionPlan || "basic";
              isUpgradePossible = role === "teach" && currentPlan === "basic";
              isPaidUser = isPaidUserTemp;
            }
            i++;
          }
        }
        setCreatorSubscription({
          ...creatorSubscription,
          plan: currentPlan,
          isUpgradePossible,
          isPaidUser,
          stripeCustomerId,
          subscriptionStatus,
          subscriptionPeriodEnd,
          isSubscriptionLoaded: true,
          verificationStatus,
        });
        return {
          ...creatorSubscription,
          plan: currentPlan,
          isUpgradePossible,
          isPaidUser,
          stripeCustomerId,
          subscriptionStatus,
          subscriptionPeriodEnd,
          isSubscriptionLoaded: true,
          verificationStatus,
        };
      }
    } catch (error) {
      logger.error(error);
      return false;
    }
  }, []);

  const cancelSubscription = async (subscriptionId) => {
    try {
      startLoading();
      await deleteSubscription(subscriptionId);
      await updateUser(user.username, {
        subscriptionPlan: "basic",
        subscriptionId: "none",
      });
      invalidateCache(DataType.USER, {
        userId: user.username,
      });
      openSnackbar("Subscription Canceled", "success");
      stopLoading();
    } catch (error) {
      openSnackbar("There was an error canceling subscription", "error");
      stopLoading();
    }
  };

  return {
    subscription,
    fetchCreatorSubscription,
    cancelSubscription,
  };
}
